import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { combineLatest, finalize, Observable, Subject, takeUntil } from 'rxjs';

import { Store } from '@ngrx/store';

import { OrganizationsLegacyService } from '../../sanus/organizations/organizations-legacy.service';
import {
	clearBulkUploadResult,
	fetchOrganizationById,
	uploadBulkActionFileStart,
} from '../../sanus/organizations/state/organizations.actions';
import {
	selectOrganizationById,
	selectOrgUploadFileResult,
	selectBulkActionId,
} from '../../sanus/organizations/state/organizations.selector';
import { FileUploadResult } from '../../shared/enums/file-upload-result.enum';
import { TemplateFileType } from '../../shared/enums/template-file.enum';
import { AuthenticationsService } from '../authentications/authentications.service';
import { AuthenticationLoginResponseDto } from '../authentications/dtos/authentication-login-response';
import { BulkActionDialogComponent } from '../beneficiaries/modals/bulk-action-upload-dialog/bulk-action-upload-dialog.component';

type BulkActionsDownloadKey =
	| 'activeBeneficiaries'
	| 'groupActiveBeneficiaries'
	| 'groupInactiveBeneficiaries'
	| 'inactiveBeneficiaries'
	| 'templateExclusion'
	| 'templateInclusion'
	| 'templateScheduledTransfer'
	| 'templateTransfer'
	| 'templateUpdate';

interface DownloadResponse {
	url: string;
}

const TEMPLATE_DOWNLOAD_KEYS: Record<TemplateFileType, BulkActionsDownloadKey> =
	{
		[TemplateFileType.EXCLUSION]: 'templateExclusion',
		[TemplateFileType.INCLUSION]: 'templateInclusion',
		[TemplateFileType.SCHEDULED_TRANSFER]: 'templateScheduledTransfer',
		[TemplateFileType.TRANSFER]: 'templateTransfer',
		[TemplateFileType.UPDATE]: 'templateUpdate',
	};

@Component({
	selector: 'app-bulk-actions',
	styleUrl: './bulk-actions.component.scss',
	templateUrl: './bulk-actions.component.html',
})
export class BulkActionsComponent implements OnInit, OnDestroy {
	@ViewChild('fileInputExclusion') fileInputExclusion?: ElementRef;
	@ViewChild('fileInputInclusion') fileInputInclusion?: ElementRef;
	@ViewChild('fileInputTransfer') fileInputTransfer?: ElementRef;
	@ViewChild('fileInputUpdate') fileInputUpdate?: ElementRef;

	actionType = TemplateFileType;
	downloadLoading: Record<BulkActionsDownloadKey, boolean> = {
		activeBeneficiaries: false,
		groupActiveBeneficiaries: false,
		groupInactiveBeneficiaries: false,
		inactiveBeneficiaries: false,
		templateExclusion: false,
		templateInclusion: false,
		templateScheduledTransfer: false,
		templateTransfer: false,
		templateUpdate: false,
	};
	isMatriz: boolean = false;
	orgId: string = '';
	userEmail: string = '';

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly store: Store,
		private readonly activatedRoute: ActivatedRoute,
		private readonly authenticationsService: AuthenticationsService,
		private readonly organizationsLegacyService: OrganizationsLegacyService,
		private readonly dialog: MatDialog,
		private readonly router: Router,
	) {}

	clearFileInputs(): void {
		if (this.fileInputInclusion) {
			this.fileInputInclusion.nativeElement.value = '';
		}

		if (this.fileInputUpdate) {
			this.fileInputUpdate.nativeElement.value = '';
		}

		if (this.fileInputTransfer) {
			this.fileInputTransfer.nativeElement.value = '';
		}

		if (this.fileInputExclusion) {
			this.fileInputExclusion.nativeElement.value = '';
		}
	}

	downloadFile(url: string): void {
		const link = document.createElement('a');
		link.setAttribute('target', '_blank');
		link.setAttribute('href', url);
		link.setAttribute('download', 'file_name.xlsx');
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	isDownloadDisabled(downloadKey: BulkActionsDownloadKey): boolean {
		return this.isAnyDownloadLoading() || this.isDownloadLoading(downloadKey);
	}

	isDownloadLoading(downloadKey: BulkActionsDownloadKey): boolean {
		return this.downloadLoading[downloadKey];
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id }) => {
			if (id) {
				this.orgId = id;
				this.store.dispatch(fetchOrganizationById({ id }));

				this.store
					.select(selectOrganizationById)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((org) => {
						if (org) {
							this.isMatriz = org.is_matriz;
						}
					});
			}
		});

		const session: AuthenticationLoginResponseDto | null =
			this.authenticationsService.getSession();

		this.userEmail = session?.email || '';

		combineLatest([
			this.store.select(selectOrgUploadFileResult),
			this.store.select(selectBulkActionId),
		])
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(([result, bulkActionId]) => {
				if (result === FileUploadResult.SUCCESS && bulkActionId) {
					const ref = this.dialog.open(BulkActionDialogComponent);
					ref.afterClosed().subscribe((navigate) => {
						if (navigate) {
							void this.router.navigate([
								'organizacoes',
								this.orgId,
								'acoes-em-massa',
								bulkActionId,
							]);
						}
					});
					this.store.dispatch(clearBulkUploadResult());
				}
			});
	}

	onActiveDataDownload(isMatriz?: boolean): void {
		const downloadKey = isMatriz
			? 'groupActiveBeneficiaries'
			: 'activeBeneficiaries';

		this.startDownload(
			downloadKey,
			() =>
				this.organizationsLegacyService.getOrgBeneDataURL(this.orgId, isMatriz),
			'Um problema ocorreu ao buscar o relatório.',
		);
	}

	onFileDownload(actionType: TemplateFileType): void {
		this.startDownload(
			TEMPLATE_DOWNLOAD_KEYS[actionType],
			() =>
				this.organizationsLegacyService.getBulkActionTemplateFileURL(
					actionType,
					this.orgId,
				),
			'Um problema ocorreu ao buscar a planilha.',
		);
	}

	onFileUpload(actionType: TemplateFileType, event: any): void {
		const request: FormData = new FormData();

		if (this.validFile(event.target.files.item(0))) {
			request.append('excel', event.target.files.item(0));
			request.append('email', this.userEmail);
			request.append('organizationId', this.orgId);
			this.store.dispatch(uploadBulkActionFileStart({ actionType, request }));
			this.clearFileInputs();
		}
	}

	onInactiveDataDownload(isMatriz?: boolean): void {
		const downloadKey = isMatriz
			? 'groupInactiveBeneficiaries'
			: 'inactiveBeneficiaries';

		this.startDownload(
			downloadKey,
			() =>
				this.organizationsLegacyService.getOrgBeneInactiveDataURL(
					this.orgId,
					isMatriz,
				),
			'Um problema ocorreu ao buscar o relatório.',
		);
	}

	validFile(file: File): boolean {
		const ext = file?.name.split('.').pop();

		return ext === 'xlsx';
	}

	private isAnyDownloadLoading(): boolean {
		return Object.values(this.downloadLoading).some(Boolean);
	}

	private startDownload(
		downloadKey: BulkActionsDownloadKey,
		getRequest: () => Observable<DownloadResponse>,
		errorMessage: string,
	): void {
		if (!this.isAnyDownloadLoading()) {
			this.downloadLoading[downloadKey] = true;

			getRequest()
				.pipe(
					takeUntil(this.unsubscribe$),
					finalize(() => (this.downloadLoading[downloadKey] = false)),
				)
				.subscribe({
					error: (error) => {
						this.organizationsLegacyService.showSnack(
							error?.error?.message ?? errorMessage,
							'error',
						);
					},
					next: ({ url }) => {
						if (url) {
							this.downloadFile(url);
						}
					},
				});
		}
	}
}
